/**
 * `GET /tenants/{t}/campaigns` and `POST /tenants/{t}/campaigns`.
 *
 * There is no producer-owned list route for campaigns in the shared
 * `handlers.ts` — only the `POST` this area already had for
 * onboarding-adjacent flows. The `GET` is built with the same `itemsOf`
 * envelope every other list route here uses; its MSW handler lives in this
 * module's own tests rather than in `handlers.ts`, so it never collides
 * with another file's edits there.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import { ME_QUERY_KEY } from '../../session/session';
import { campaignSchema, type Campaign, type CampaignSummary } from '../api/schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

export const CAMPAIGNS_QUERY_KEY = (tenantId: string) => ['tenant', tenantId, 'campaigns'] as const;

export function useCampaigns(tenantId: string): UseQueryResult<CampaignSummary[], ApiFailureError> {
  const client = useApiClient();

  return useQuery<CampaignSummary[], ApiFailureError>({
    queryKey: CAMPAIGNS_QUERY_KEY(tenantId),
    queryFn: async () => {
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.tenantCampaigns, { tenantId }),
        GET_ROUTES.tenantCampaigns.schema,
      );
      return items;
    },
  });
}

export function useCreateCampaign(
  tenantId: string,
): UseMutationResult<
  Campaign,
  ApiFailureError,
  { name: string; timezone: string; active_from?: string; active_to?: string }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) =>
      client.postParsed(`/tenants/${tenantId}/campaigns`, input, campaignSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY(tenantId) });
      // The new campaign is listed in the switcher only once `/control/me` is asked again.
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}
